import { useEffect, useMemo, useRef } from "react";
import { connect } from "react-redux";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";

const customStyles = {
  menu: (base) => ({ ...base, zIndex: 9999 }),
  valueContainer: (baseStyles) => ({
    ...baseStyles,
    maxHeight: "3.5vw",
    overflowY: "auto",
  }),
  control: (baseStyles) => ({ ...baseStyles, flexWrap: "nowrap" }),
};

const operatingCountries = ["MX", "CO", "PE", "US"]

const PACKAGING_TYPE_OPTIONS = [
  { value: "BOLSA", label: "Bolsa", abbreviation: "B" },
  { value: "CAJA", label: "Caja", abbreviation: "C" },
];

const CreatePackagingModalComponent = ({
  show,
  handleClose,
  successfulCreatePackaging = false,
  reduxCreatePackaging = () => {},
  countries = {},
  reduxGetCountries = () => {},
  logisticsOperators = {},
  reduxGetLogisticsOperators = () => {},
}) => {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm();

  const watchCountryId = watch("countryId");
  const watchPackagingType = watch("packagingType");
  const watchCode = watch("code");

  const isAvailableToSubmit =
    watchCountryId &&
    watchPackagingType &&
    watchCode &&
    watch("name");

  const userData = useMemo(() => {
    return JSON.parse(window.localStorage.getItem('userData')) || {};
  }, []);

  const allowAllCountries = userData.allowAllCountries !== false;
  const userCountryId = userData.idCountry || null;
  const forcedCountryId = !allowAllCountries ? userCountryId : null;

  const countriesItems = useMemo(
    () => {
      let items = Array.isArray(countries?.items) ? countries.items : [];
      if (!allowAllCountries && forcedCountryId) {
        items = items.filter((c) => c.idCountry === forcedCountryId);
      } else if (items.some((c) => c.isoCode)) {
        items = items.filter((c) => operatingCountries.includes(c.isoCode));
      }
      return items;
    },
    [countries.items, allowAllCountries, forcedCountryId],
  );

  const countryOptions = useMemo(
    () =>
      countriesItems.map((country) => ({
        value: country.idCountry,
        label: country.description,
        isoCode: country.isoCode,
      })),
    [countriesItems],
  );

  const measurementUnit = useMemo(() => {
    if (!watchCountryId) return "cm";
    const country = countryOptions.find((c) => c.value === watchCountryId);
    if (!country) return "cm";
    // USA usa pulgadas; LATAM usa centimetros
    return country.isoCode === "US" ? "in" : "cm";
  }, [watchCountryId, countryOptions]);

  const codePrefix = useMemo(() => {
    if (!watchCountryId || !watchPackagingType) return "";
    const country = countryOptions.find((c) => c.value === watchCountryId);
    const typeOption = PACKAGING_TYPE_OPTIONS.find(
      (t) => t.value === watchPackagingType,
    );
    if (!country?.isoCode || !typeOption?.abbreviation) return "";
    return country.isoCode + typeOption.abbreviation;
  }, [watchCountryId, watchPackagingType, countryOptions]);

  useEffect(() => {
    if (show === false) return;
    reset({
      packagingType: null,
      name: "",
      code: "",
      length: "",
      width: "",
      height: "",
      tolerancePercentage: "",
      operatorId: null,
    });
    setValue("countryId", forcedCountryId || userCountryId);
    reduxGetCountries({
      page: 1,
      offset: 1000,
      search: "isActive|true",
    });
    reduxGetLogisticsOperators({ includeAll: true });
  }, [show, reduxGetCountries, reduxGetLogisticsOperators, reset, forcedCountryId, userCountryId, setValue]);

  useEffect(() => {
    if (!codePrefix) return;

    const currentCode = watchCode || "";
    const currentPrefix = currentCode.slice(0, 3);

    if (currentPrefix !== codePrefix) {
      const suffix = currentCode.length > 3 ? currentCode.slice(3) : "";
      setValue("code", codePrefix + suffix, { shouldDirty: true });
    }
  }, [codePrefix, watchCode, setValue]);

  const prevSuccessfulCreate = useRef(successfulCreatePackaging);

  useEffect(() => {
    if (successfulCreatePackaging && !prevSuccessfulCreate.current) {
      reset({
        packagingType: null,
        name: "",
        code: "",
        length: "",
        width: "",
        height: "",
        tolerancePercentage: "",
        operatorId: null,
        countryId: null,
      });
      handleClose();
    }
    prevSuccessfulCreate.current = successfulCreatePackaging;
  }, [successfulCreatePackaging, reset, handleClose]);

  const onSubmit = (data) => {
    reduxCreatePackaging({
      countryId: data.countryId,
      packagingType: data.packagingType,
      code: data.code,
      name: data.name.trim(),
      length: data.length,
      width: data.width,
      height: data.height,
      tolerancePercentage: data.tolerancePercentage,
      operatorId: data.operatorId,
    });
  };

  return (
      <Modal
        size="lg"
        show={show}
        onHide={handleClose}
        onEscapeKeyDown={handleClose}
        backdrop="static"
        keyboard={true}
      >
        <Modal.Header closeButton>
          <Modal.Title>Crear Embalaje</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body>
            <Row>
              <Form.Group as={Col} className="px-3 pb-2">
                <label htmlFor="countryId" className="form-label">País*</label>
                <Controller
                  name="countryId"
                  control={control}
                  rules={{ required: "Es obligatorio seleccionar un país" }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      className={`${!!errors?.countryId && "is-invalid"}`}
                      styles={customStyles}
                      options={countryOptions}
                      isDisabled={!allowAllCountries}
                      value={
                        field.value
                          ? countryOptions.find(
                              (option) => option.value === field.value,
                            )
                          : null
                      }
                      onChange={(selectedOption) => {
                        field.onChange(
                          selectedOption ? selectedOption.value : null,
                        );
                        setValue("operatorId", null);
                      }}
                    />
                  )}
                />
                {!!errors?.countryId && (
                  <span className="error-message">
                    {errors?.countryId?.message || "Este campo es obligatorio"}
                  </span>
                )}
              </Form.Group>
            </Row>
            <Row>
              <Form.Group as={Col} className="px-3 pb-2">
                <label htmlFor="packagingType" className="form-label">
                  Tipo de Embalaje*
                </label>
                <Controller
                  name="packagingType"
                  control={control}
                  rules={{
                    required: "Es obligatorio seleccionar un tipo de embalaje",
                  }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      className={`${!!errors?.packagingType && "is-invalid"}`}
                      styles={customStyles}
                      options={PACKAGING_TYPE_OPTIONS}
                      value={
                        field.value
                          ? PACKAGING_TYPE_OPTIONS.find(
                              (option) => option.value === field.value,
                            )
                          : null
                      }
                      onChange={(selectedOption) =>
                        field.onChange(
                          selectedOption ? selectedOption.value : null,
                        )
                      }
                    />
                  )}
                />
                {!!errors?.packagingType && (
                  <span className="error-message">
                    {errors?.packagingType?.message ||
                      "Este campo es obligatorio"}
                  </span>
                )}
              </Form.Group>
            </Row>
            <Row>
              <Form.Group as={Col} className="px-3 pb-2">
                <label htmlFor="name" className="form-label">Nombre*</label>
                <input
                  {...register("name", {
                    required: "El nombre es obligatorio",
                    maxLength: {
                      value: 100,
                      message: "Máximo 100 caracteres permitidos",
                    },
                    minLength: {
                      value: 4,
                      message: "Mínimo 4 caracteres permitidos",
                    },
                    pattern: {
                      value: /^[a-zA-ZáéíóúÁÉÍÓÚñÑ0-9]+$/,
                      message: "Solo se permiten letras y números",
                    },
                  })}
                  id="name"
                  type="text"
                  className={`form-control ${!!errors?.name && "is-invalid"}`}
                />
                {!!errors?.name && (
                  <span className="error-message">
                    {errors?.name?.message || "Este campo es obligatorio"}
                  </span>
                )}
              </Form.Group>
            </Row>
                        <Row>
              <Form.Group as={Col} className="px-3 pb-2">
                <label htmlFor="code-suffix" className="form-label">Código*</label>
                <div className="d-flex">
                  {codePrefix ? (
                    <span
                      className="form-control bg-light text-muted d-inline-flex align-items-center justify-content-center"
                      style={{
                        maxWidth: "70px",
                        borderRight: "none",
                        cursor: "not-allowed",
                        fontWeight: 600,
                        letterSpacing: "1px",
                      }}
                    >
                      {codePrefix}
                    </span>
                  ) : null}
                  <Controller
                    name="code"
                    control={control}
                    rules={{
                      required: "El código es obligatorio",
                      validate: (value) =>
                        /^[A-Z]{2}[BC]\d{3}$/.test(value) ||
                        "Debe completar los 3 dígitos (ej. COB001)",
                    }}
                    render={({ field }) => (
                      <input
                        id="code-suffix"
                        disabled={!codePrefix}
                        placeholder={
                          codePrefix ? "" : "Seleccione país y tipo primero"
                        }
                        maxLength={3}
                        onChange={(e) => {
                          const digits = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 3);
                          field.onChange(codePrefix + digits);
                        }}
                        value={field.value ? field.value.slice(3) : ""}
                        className={`form-control ${
                          !!errors?.code && "is-invalid"
                        }`}
                        style={
                          codePrefix
                            ? {
                                borderTopLeftRadius: 0,
                                borderBottomLeftRadius: 0,
                              }
                            : {}
                        }
                      />
                    )}
                  />
                </div>
                {!!errors?.code && (
                  <span className="error-message">
                    {errors?.code?.message || "Este campo es obligatorio"}
                  </span>
                )}
              </Form.Group>
            </Row>
            <Row>
              <Form.Group as={Col} md={4} className="px-3 pb-2">
                <label htmlFor="length" className="form-label">Largo ({measurementUnit})*</label>
                <input
                  {...register("length", {
                    required: "El largo es obligatorio",
                    min: { value: 0.01, message: "El valor mínimo es 0.01" },
                    valueAsNumber: true,
                    validate: (value) =>
                      /^\d+(\.\d{1,2})?$/.test(String(value)) ||
                      "Máximo 2 decimales permitidos",
                  })}
                  id="length"
                  type="number"
                  step="any"
                  className={`form-control ${!!errors?.length && "is-invalid"}`}
                />
                {!!errors?.length && (
                  <span className="error-message">
                    {errors?.length?.message || "Valor inválido"}
                  </span>
                )}
              </Form.Group>
              <Form.Group as={Col} md={4} className="px-3 pb-2">
                <label htmlFor="width" className="form-label">Ancho ({measurementUnit})*</label>
                <input
                  {...register("width", {
                    required: "El ancho es obligatorio",
                    min: { value: 0.01, message: "El valor mínimo es 0.01" },
                    valueAsNumber: true,
                    validate: (value) =>
                      /^\d+(\.\d{1,2})?$/.test(String(value)) ||
                      "Máximo 2 decimales permitidos",
                  })}
                  id="width"
                  type="number"
                  step="any"
                  className={`form-control ${!!errors?.width && "is-invalid"}`}
                />
                {!!errors?.width && (
                  <span className="error-message">
                    {errors?.width?.message || "Valor inválido"}
                  </span>
                )}
              </Form.Group>
              <Form.Group as={Col} md={4} className="px-3 pb-2">
                <label htmlFor="height" className="form-label">Alto ({measurementUnit})*</label>
                <input
                  {...register("height", {
                    required: "El alto es obligatorio",
                    min: { value: 0.01, message: "El valor mínimo es 0.01" },
                    valueAsNumber: true,
                    validate: (value) =>
                      /^\d+(\.\d{1,2})?$/.test(String(value)) ||
                      "Máximo 2 decimales permitidos",
                  })}
                  id="height"
                  type="number"
                  step="any"
                  className={`form-control ${!!errors?.height && "is-invalid"}`}
                />
                {!!errors?.height && (
                  <span className="error-message">
                    {errors?.height?.message || "Valor inválido"}
                  </span>
                )}
              </Form.Group>
            </Row>
            <Row>
              <Form.Group as={Col} md={6} className="px-3 pb-2">
                <label htmlFor="tolerancePercentage" className="form-label">
                  Tolerancia % (máx 50)*
                </label>
                <input
                  {...register("tolerancePercentage", {
                    required: "La tolerancia es obligatoria",
                    min: { value: 0, message: "El valor mínimo es 0" },
                    max: { value: 50, message: "El valor máximo es 50%" },
                    valueAsNumber: true,
                  })}
                  id="tolerancePercentage"
                  type="number"
                  step="0.01"
                  className={`form-control ${!!errors?.tolerancePercentage && "is-invalid"}`}
                />
                {!!errors?.tolerancePercentage && (
                  <span className="error-message">
                    {errors?.tolerancePercentage?.message || "Valor inválido"}
                  </span>
                )}
              </Form.Group>
              <Form.Group as={Col} md={6} className="px-3 pb-2">
                <label htmlFor="operatorId" className="form-label">
                  Operador
                </label>
                <Controller
                  name="operatorId"
                  control={control}
                  render={({ field }) => {
                    const items = Array.isArray(logisticsOperators?.items)
                      ? logisticsOperators.items
                      : [];
                    const options = items
                      .filter((f) => f.isActive === true)
                      .filter((f) => !watchCountryId || f.country === watchCountryId)
                      .map((ele) => ({
                        value: ele.idCompany,
                        label: ele.description,
                      }));
                    return (
                      <Select
                        {...field}
                        isClearable
                        styles={customStyles}
                        options={options}
                        value={
                          field.value
                            ? options.find((o) => o.value === field.value)
                            : null
                        }
                        onChange={(selectedOption) =>
                          field.onChange(
                            selectedOption ? selectedOption.value : null,
                          )
                        }
                      />
                    );
                  }}
                />
              </Form.Group>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={!isAvailableToSubmit}
            >
              Guardar
            </Button>
          </Modal.Footer>
        </form>
        
      </Modal>
  );
};

const mapStateToProps = (state) => ({
  successfulCreatePackaging: state.packagingState.successfulCreatePackaging,
  countries: state.countryState.countries,
  logisticsOperators: state.companyState.logisticsOperators,
});

const mapDispatchToProps = (dispatch) => ({
  reduxCreatePackaging: (payload) =>
    dispatch({ type: "CREATE_PACKAGING_REQUEST", value: payload }),
  reduxGetCountries: (payload) =>
    dispatch({ type: "FETCH_COUNTRIES_REQUEST", value: payload }),
  reduxGetLogisticsOperators: (payload) =>
    dispatch({
      type: "FETCH_COMPANIESBYLOGISTICSOPERATOR_REQUEST",
      value: payload,
    }),
});

export const CreatePackagingModal = connect(
  mapStateToProps,
  mapDispatchToProps,
)(CreatePackagingModalComponent);
